import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaPlus, FaChevronDown, FaChevronRight, FaExternalLinkAlt } from "react-icons/fa";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import Pagination from  '../../../components/Pagination/Pagination';
import { API } from 'aws-amplify';
import '../../../styles/compact-filters.css';
import '../../../styles/ops-list.css';
import '../../../components/PageHeader/PageHeader.css';
import PageHeader from '../../../components/PageHeader/PageHeader';
import { useI18n } from '../../../i18n';
import { ASSET_TYPE } from '../../../localdb';
import './AssetList.css';

const PAGE_SIZE_KEY = "assetListPageSize";
const PAGE_SIZES = [10, 25, 50];

function assetTypeIndex(type) {
	if (!type) return ASSET_TYPE.length - 1;
	const exact = ASSET_TYPE.indexOf(type);
	if (exact >= 0) return exact;
	const lower = String(type).toLowerCase();
	const fuzzy = ASSET_TYPE.findIndex((name) => lower.includes(name.toLowerCase()));
	return fuzzy >= 0 ? fuzzy : ASSET_TYPE.length - 1;
}

function equipmentBits(item) {
	return [item.controller, item.signal_head, item.halogen_led, item.push_button].filter(Boolean);
}

const AssetList = (props) => {
	const { t, tCatalog } = useI18n();
	const [assets, setAssets] = useState([]);
	const [assetList, setAssetList] = useState([]);
	
	const [district, setDistrict] = useState('');
	const [siteRef,setSiteRef] = useState('');
	const [search, setSearch] = useState('');
	const [isLoading, setIsLoading] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(() => {
		const n = parseInt(localStorage.getItem(PAGE_SIZE_KEY), 10);
		return PAGE_SIZES.includes(n) ? n : 10;
	});
	const [expandedId, setExpandedId] = useState(null);
	const [districtList, setDistrictList] = useState([]);

    const params = { 
        headers: {
			'x-api-key': process.env.REACT_APP_API_KEY,        
			'Accept': 'application/json',
			'Content-Type': 'application/json',
        },
	}
	
	// Load assets
	useEffect(()=>{
		const fetchAssets = async () => {
			setIsLoading(true);
			const assets = await API.get('assets','/', params)
			
			for (let i = 0; i < assets.length; i++) {
				if (assets[i].hp_site === true) {
					assets[i].hp_site_str = "High"
				}else{
					assets[i].hp_site_str = "Low"
				}
			}

			assets.sort((a, b) => (a.site_ref > b.site_ref) ? 1 : -1)

			setAssets(assets);
			setAssetList(assets);
			setIsLoading(false);			
		}
		fetchAssets();	  			
	}, []);

	useEffect(() => {
		handleSearch();
		}, [district, siteRef, search]); 

	// Load district
	useEffect(()=>{
		const fetchDistrict = async () => {
			
			const districts = await API.get('settings','/DISTRICTS', params)
			// districts.values.sort((a, b) => (a > b) ? 1 : -1)

			const unique = [...new Set((districts.values || []).filter(Boolean))];
			const list = unique.map((name) => (
				<option key={name} value={name}>{name}</option>
			));

			setDistrictList(list);
		}
		fetchDistrict();	 
	}, []);
	
	const handleClearSearch = () =>{
		setDistrict('');
		setSiteRef('');
		setSearch('');
	}

	const handleSearch = () => { 		
		let results = assets;

		if (siteRef){
			results = results.filter(item =>
				item.site_ref && item.site_ref.toLowerCase().includes(siteRef.toLowerCase())
		)}

		if (district){
			results = results.filter(item =>
				item.district && item.district.toLowerCase().includes(district.toLowerCase())
		)}

		const query = search.trim().toLowerCase();
		if (query) {
			results = results.filter(item =>
				[item.site_ref, item.site_address, item.district, item.type, item.charge_code]
					.join(' ')
					.toLowerCase()
					.includes(query)
			);
		}

		setAssetList(results);
		setCurrentPage(1);
	}

	useEffect(() => {
		localStorage.setItem(PAGE_SIZE_KEY, String(pageSize));
		setCurrentPage(1);
	}, [pageSize]);

	const indexOfLastItem = currentPage * pageSize;
	const indexOfFirstItem = indexOfLastItem - pageSize;
	const currentItemList = assetList.slice(indexOfFirstItem, indexOfLastItem);
	const rangeStart = assetList.length === 0 ? 0 : indexOfFirstItem + 1;
	const rangeEnd = Math.min(indexOfLastItem, assetList.length);

	const paginate = (pageNumber) => {
		setCurrentPage(pageNumber);
		setExpandedId(null);
	};

	const toggleExpand = (assetId) => {
		setExpandedId((current) => (current === assetId ? null : assetId));
	};

	
	const renderRow = (item) => {
		const open = expandedId === item.assetId;
		const address = item.site_address || t('assets.noAddress');
		const isPriority = item.hp_site === true || item.hp_site_str === 'High';
		const typeIndex = assetTypeIndex(item.type);
		const kit = equipmentBits(item);
		return (
			<article
				key={item.assetId}
				className={`task-ops-item${open ? ' is-open' : ''}`}
				onClick={() => toggleExpand(item.assetId)}
			>
				<div className="task-ops-row" role="button" tabIndex={0} aria-expanded={open}
					onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpand(item.assetId); } }}>
					<div className="task-ops-identity">
						<Link className="task-ops-id" to={`/asset_det/${item.assetId}`} onClick={(e) => e.stopPropagation()}>
							{item.site_ref}
						</Link>
						{item.type && (
							<span className={`task-ops-status asset-type asset-type-${typeIndex}`} title={tCatalog(item.type)}>
								<span className="asset-type-pin" aria-hidden="true" />
								{tCatalog(item.type)}
							</span>
						)}
						{isPriority && (
							<span className="task-ops-flag" title={t('assets.prioritySite')}>
								<span className="task-ops-flag-dot" aria-hidden="true" />
								{t('assets.priority')}
							</span>
						)}
						{item.elv_lv && <span className="task-ops-date">{item.elv_lv}</span>}
					</div>
					<button type="button" className="task-ops-toggle" aria-label={open ? t('assets.collapse') : t('assets.expand')}
						onClick={(e) => { e.stopPropagation(); toggleExpand(item.assetId); }}>
						{open ? <FaChevronDown /> : <FaChevronRight />}
					</button>
					<p className="task-ops-title" title={address}>{address}</p>
					<div className="task-ops-meta">
						<div className="task-ops-place">
							{item.district && <span className="task-ops-site-code">{item.district}</span>}
							{kit.map((bit, i) => (
								<span key={`${bit}-${i}`} className="task-ops-cat">· {bit}</span>
							))}
						</div>
					</div>
				</div>
				{open && (
					<div className="task-ops-detail">
						<div className="task-ops-detail-inner">
							<dl>
								<div className="is-wide">
									<dt>{t('common.address')}</dt>
									<dd>{address}{item.district ? ` · ${item.district}` : ''}</dd>
								</div>
								<div>
									<dt>{t('fields.controller')}</dt>
									<dd>{item.controller || '—'}</dd>
								</div>
								<div>
									<dt>{t('fields.signalHead')}</dt>
									<dd>{item.signal_head || '—'}</dd>
								</div>
								<div>
									<dt>{t('fields.halogenLed')}</dt>
									<dd>{item.halogen_led || '—'}</dd>
								</div>
								<div>
									<dt>{t('fields.pushButton')}</dt>
									<dd>{item.push_button || '—'}</dd>
								</div>
								<div>
									<dt>{t('fields.elvLv')}</dt>
									<dd>{item.elv_lv || '—'}</dd>
								</div>
								<div>
									<dt>{t('fields.chargeCode')}</dt>
									<dd>{item.charge_code || '—'}</dd>
								</div>
								<div>
									<dt>{t('assets.priority')}</dt>
									<dd>{item.hp_site_str === 'High' ? t('assets.high') : item.hp_site_str === 'Low' ? t('assets.low') : (item.hp_site_str || '—')}</dd>
								</div>
							</dl>
							<div className="task-ops-actions">
								<Link className="task-ops-open" to={`/asset_det/${item.assetId}`} onClick={(e) => e.stopPropagation()}>
									<FaExternalLinkAlt /> {t('actions.openAsset')}
								</Link>
							</div>
						</div>
					</div>
				)}
			</article>
		);
	};

	return (
		<div className="AssetList">
			<PageHeader
				title={t('pages.assets.title')}
				subtitle={t('pages.assets.subtitle')}
				breadcrumbs={[{ label: t('nav.assets'), path: '/assets' }]}
				count={!isLoading ? assetList.length : undefined}
				countLabel={t('assets.countLabel')}
				actions={
					<Button size="sm" variant="success" onClick={() => { props.history.push('/asset_add/'); }}>
						<FaPlus /> {t('actions.addAsset')}
					</Button>
				}
			/>
			<div className="task-ops">
				<Form className="compact-filters task-ops-filters" onSubmit={(e) => e.preventDefault()}>
					<div className="task-ops-filters-main">
						<Form.Control className="task-ops-search" size="sm" placeholder={t('assets.searchPlaceholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
						<Form.Control as="select" size="sm" value={district} onChange={(e) => setDistrict(e.target.value)}>
							<option value="">{t('common.district')}</option>
							{districtList}
						</Form.Control>
						<button type="button" className="task-ops-reset" onClick={handleClearSearch}>{t('common.reset')}</button>
					</div>
				</Form>
				<div className="task-ops-panel" aria-busy={isLoading}>
					{isLoading ? (
						<div className="loading-container">
							<div className="loading-content">
								<Spinner animation="border" role="status" className="loading-spinner" />
								<div className="loading-text">{t('assets.loading')}</div>
							</div>
						</div>
					) : (
						<>
							<div className="task-ops-legend" aria-hidden="true">{t('assets.legend')}</div>
							<div className="task-ops-list">
								{currentItemList.length === 0 ? (
									<div className="task-ops-empty">{t('assets.empty')}</div>
								) : currentItemList.map(renderRow)}
							</div>
							<div className="task-ops-footer">
								<p className="task-ops-range">{t('assets.showing', { start: rangeStart, end: rangeEnd, total: assetList.length })}</p>
								<div className="task-ops-pagesize">
									<label htmlFor="asset-pagesize">{t('common.rows')}</label>
									<select id="asset-pagesize" value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
										{PAGE_SIZES.map((size) => <option key={size} value={size}>{size}</option>)}
									</select>
								</div>
								<Pagination sizePerPage={pageSize} currentPage={currentPage} totalSize={assetList.length} changeCurrentPage={paginate} theme="bootstrap" />
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
}


export default AssetList;
